/**
 * Demo data hooks.
 * This file exists to break circular dependencies between play-context.tsx and benchmark.selectors.ts
 */

import { useSelector } from 'react-redux';
import { createGlobalState } from 'react-use';
import { selectApp } from '@bbkAdminRedux/app/selectors';
import { selectCurrentApp } from '@bbkAdminRedux/rtkq/current-user.slice';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { useGetProductLicense } from '@bbkAdminUtils/plans';
import { useLocation } from '@bbkShared/react-router';

export const useDisableDemoReports = createGlobalState<boolean>(false);

export const useShowDemoData = () => {
  const productLicense = useGetProductLicense();
  const [globalDisableDemoReports] = useDisableDemoReports();

  const disableDemoReports = productLicense.growthV2License
    ? globalDisableDemoReports
    : false;
  const demoDataEnabled = useFeatureFlags(FeatureFlagsE.ENABLE_DEMO_REPORTS);
  const app = useSelector(selectApp);

  return (app?.show_demo_data || demoDataEnabled) && !disableDemoReports;
};

const NEW_PLAY_PATH_PATTERN = new RegExp(`${AuthorizedPaths.playNew}(/|$)`);
const UNIFIED_NEW_PLAY_PATH_PATTERN = new RegExp(
  `${AuthorizedPaths.unifiedPlaysNew}(/|$)`
);

export const useIsNewPlayPage = () => {
  const location = useLocation();
  return (
    NEW_PLAY_PATH_PATTERN.test(location.pathname) ||
    UNIFIED_NEW_PLAY_PATH_PATTERN.test(location.pathname)
  );
};

export const useShowPlayDemoData = () => {
  const app = useSelector(selectCurrentApp);
  const demoDataEnabled = useFeatureFlags(FeatureFlagsE.ENABLE_DEMO_REPORTS);
  const isNewPlayPage = useIsNewPlayPage();
  const [globalDisableDemoReports] = useDisableDemoReports();

  if (isNewPlayPage) {
    return false;
  }

  return (
    (app?.show_play_demo_data || demoDataEnabled) && !globalDisableDemoReports
  );
};
